import React from 'react'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { conformToMask } from 'react-text-mask'
import styled from 'styled-components'

import {
  Box,
  ButtonGroup,
  ProgressIndicator,
  H1,
  H2,
} from '@allied-solutions/affinity'

import {
  useAddFormValuesToState,
  useCiamUpdateProfile,
  usePageData,
  useRedirectToEnterRefId,
  useStore,
  useSubmitInsuranceLogic,
  useLeaseTypeLoan,
} from '../hooks'
import { AppLayout, BackButton, NextButton, InfoBox } from './AppLayout'

import { StepNumber } from './StepNumber'
import { SubmitInsuranceLayout } from './SubmitInsuranceLayout'

const StyledButtonGroup = styled(ButtonGroup)`
  flex-direction: row;
  gap: 1.5rem;

  & > button:last-child {
    margin-bottom: 0;
  }
`

const FormLayout = ({
  location,
  leftSide,
  rightSide,
  initialValues,
  mergeInitialValues = true,
  validationSchema,
  handleSubmit: handleSubmitProp,
}) => {
  useRedirectToEnterRefId()
  const { state } = useStore()
  const { handleUpdateProfile } = useCiamUpdateProfile()
  const addFormValuesToState = useAddFormValuesToState()
  const { handleSubmit, pages } = useSubmitInsuranceLogic(location)
  const isClientLeaseType = useLeaseTypeLoan()
  const { pageSlug } = usePageData(location)

  const initialValuesFn = () => {
    if (mergeInitialValues) {
      //const borrowerCopy = { ...state.borrower }
      let borrowerCopy = removeEditableValuesFn()
      const phoneNumber = borrowerCopy.AgentTelephone
      const phoneNumberMask = [
        '(',
        /[1-9]/,
        /\d/,
        /\d/,
        ')',
        ' ',
        /\d/,
        /\d/,
        /\d/,
        '-',
        /\d/,
        /\d/,
        /\d/,
        /\d/,
      ]
      if (phoneNumber) {
        conformToMask(phoneNumber, phoneNumberMask, { guide: false })
        //borrowerCopy.AgentTelephone = conformedPhoneNumber.conformedValue
      }
      return {
        ...initialValues,
        ...borrowerCopy,
        ...state.form,
      }
    }
    return initialValues
  }

  const removeEditableValuesFn = () => {
    const borrowerCopy = { ...state.borrower }
    borrowerCopy.InsuranceCompanyName = ''
    borrowerCopy.PolicyNumber = ''
    borrowerCopy.AgentName = ''
    borrowerCopy.AgentTelephone = ''
    borrowerCopy.CompDeductible = ''
    borrowerCopy.CollDeductible = ''
    borrowerCopy.EffectiveDate = ''
    borrowerCopy.ExpirationDate = ''
    if (isClientLeaseType) {
      borrowerCopy.LiabAmount_PDSL = ''
      borrowerCopy.LiabAmount_BIPP = ''
      borrowerCopy.LiabAmount_BIPA = ''
      borrowerCopy.LiabAmount_ACSL = ''
    }
    return {
      ...borrowerCopy,
    }
  }

  return (
    <SubmitInsuranceLayout location={location}>
      <Formik
        initialValues={initialValuesFn()}
        onSubmit={async (values, formikHelpers) => {
          try {
            if (
              state.hasSessionToken &&
              pageSlug === 'review' &&
              typeof window !== 'undefined'
            ) {
              const refIdStored = localStorage.getItem('refId')
              if (refIdStored) {
                const profileUpdated = await handleUpdateProfile({
                  RefId: { Add: [refIdStored] },
                })
                if (!profileUpdated) {
                  throw new Error(
                    'Unable to update your profile. Please try again.'
                  )
                }
                localStorage.removeItem('refId')
              }
            }
            const submitFn = handleSubmitProp || handleSubmit
            return await submitFn(values, formikHelpers)
          } catch (error) {
            console.error('Form submission error:', error)
            let errorMessage = 'An error occurred during submission'

            // Check if we're in a browser environment before using browser-specific APIs
            if (
              typeof window !== 'undefined' &&
              typeof document !== 'undefined' &&
              error.body?.getReader
            ) {
              try {
                const reader = error.body?.getReader()
                const { value } = await reader.read()
                const text =
                  typeof TextDecoder === 'undefined'
                    ? ''
                    : new TextDecoder().decode(value)
                const data = JSON.parse(text)
                errorMessage = data.message || errorMessage
              } catch (readError) {
                console.error('Error reading error stream:', readError)
              }
            } else if (error.message) {
              errorMessage = error.message
            }

            formikHelpers.setStatus({ error: errorMessage.toString() })
            formikHelpers.setSubmitting(false)
          }
        }}
        validate={addFormValuesToState}
        validationSchema={Yup.object().shape(validationSchema)}
      >
        {props => (
          <Form noValidate>
            <AppLayout
              leftSide={
                leftSide.replace ? (
                  leftSide.replace(props)
                ) : (
                  <>
                    {leftSide.subhead || (
                      <StepNumber pages={pages} location={location} />
                    )}
                    <H1 typeStyle="headlineMedium" fontWeight={900}>
                      {leftSide.title}
                    </H1>
                    {leftSide.body}
                  </>
                )
              }
              rightSide={
                rightSide.replace ? (
                  rightSide.replace(props)
                ) : (
                  <InfoBox>
                    <H2
                      className="titleSmall"
                      typeStyle="titleSmall"
                      fontWeight={900}
                      textAlign="left"
                    >
                      {rightSide.title}
                    </H2>
                    {rightSide.fields(props)}
                  </InfoBox>
                )
              }
              footerButtons={
                <Box display="flex" flexDirection="column" width="100%">
                  {props.status?.error && (
                    <div
                      style={{
                        backgroundColor: '#FEE2E2',
                        color: '#B91C1C',
                        padding: '12px',
                        borderRadius: '4px',
                        marginBottom: '12px',
                      }}
                    >
                      {props.status?.error}
                    </div>
                  )}
                  <Box
                    display="flex"
                    alignItems="baseline"
                    justifyContent="flex-end"
                    gap={8}
                    width="100%"
                  >
                    <ProgressIndicator
                      pages={pages}
                      currentPage={location.pathname}
                    />
                    <StyledButtonGroup
                      display="flex"
                      justifyContent={[null, null, null, 'flex-end']}
                      flexDirection="row"
                    >
                      <BackButton />
                      <NextButton formikProps={props}>
                        {pageSlug === 'review' ? 'Submit' : 'Next'}
                      </NextButton>
                    </StyledButtonGroup>
                  </Box>
                </Box>
              }
            />
          </Form>
        )}
      </Formik>
    </SubmitInsuranceLayout>
  )
}

export { FormLayout }
